import type { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type { IAppConfig } from '@core/common-legacy';
import { APP_CONFIG, AUTH_METHOD, AUTH_TOKEN, COMPANY_ID, ENVIRONMENT } from '@core/common-legacy';
import {
  HttpApiRequestOptions,
  HttpRequestData,
  IdentityHeader,
  IdentityHeaders,
} from '@core/http-types';

import type { AppEnvironment } from '../../environments/interface';

@Injectable()
export class RecruiteeRequestData extends HttpRequestData {
  public static getIdentityHeaders = (
    environment: AppEnvironment,
    isE2e: boolean,
  ): Partial<Record<IdentityHeader, string>> => {
    const realtimeEvent = Zone.current.get('realtime-event');
    const isStartup = !!Zone.current.get('startup');
    const realtimeHeaders = realtimeEvent ? { [IdentityHeader.RealtimeEvent]: realtimeEvent } : {};
    const startupHeaders = isStartup ? { [IdentityHeader.Startup]: 'true' } : {};
    return {
      [IdentityHeader.Client]: isE2e ? 'WebApp - e2e' : 'WebApp',
      [IdentityHeader.Env]: environment.environmentName,
      ...realtimeHeaders,
      ...startupHeaders,
    };
  };

  private readonly authToken = inject<string>(AUTH_TOKEN);
  private readonly appConfig = inject<IAppConfig>(APP_CONFIG);
  protected readonly environment = inject<AppEnvironment>(ENVIRONMENT);
  protected readonly currentCompanyId = inject<number>(COMPANY_ID);
  public readonly authMethod = inject<string>(AUTH_METHOD);

  constructor() {
    super();
  }

  public buildUrl(path: string = '', options: HttpApiRequestOptions = {}): string {
    let url = this.appConfig.apiUrl;

    if (this.authToken && this.currentCompanyId && !options.skipCompany) {
      url += `c/${this.currentCompanyId}/`;
    }

    let parsedPath = path;
    if (path[0] === '/') {
      parsedPath = path.substring(1);
    }

    return url + parsedPath;
  }

  public provideAuthHeaders(): { Authorization: string } | {} {
    return this.authMethod && this.authToken
      ? { Authorization: `${this.authMethod} ${this.authToken}` }
      : {};
  }

  public provideIdentityHeaders(): IdentityHeaders {
    return RecruiteeRequestData.getIdentityHeaders(this.environment, this.appConfig.isE2e);
  }

  public buildSignedUrl(path: string, searchHttpParams?: HttpParams): string {
    let url = `${this.appConfig.authUrl}f/c/${this.currentCompanyId}/`;

    let parsedPath = path;
    if (path[0] === '/') {
      parsedPath = path.substring(1);
    }

    url = url + parsedPath;

    return searchHttpParams ? `${url}?${searchHttpParams.toString()}` : url;
  }
}
